import {Typography} from '@mui/material';
import classNames from 'classnames';
import * as React from 'react';
import {useCallback} from 'react';
import {FormattedMessage, defineMessages, useIntl} from 'react-intl';

import styles from './StatusBox.less';
import {statusSlice} from '../status_slice';
import {TranslateUtils} from 'src/js/common/utils';
import {DlMuiPopper} from 'src/js/components/DlMuiPopper';
import {RemoveButton} from 'src/js/components/RemoveButton';
import {useMuiAnchor} from 'src/js/hooks';
import {DlError} from 'src/js/model/error';
import {useAppDispatch} from 'src/js/store_hooks';

const DUMMY_ERROR = {};

const I18N = defineMessages({
  CLEAR_ERROR: {
    id: 'status.error.clear',
    defaultMessage: 'Clear reported errors',
    description: 'title text for clear application errors button',
  },
  REPORTED_ERRORS: {
    id: 'status.error.reported',
    defaultMessage: 'Total errors: {errorsLength}, the latest: {latestError}',
    description: 'Error report, containing number of errors and latest error',
  },
});

interface OwnProps {
  errors: DlError[];
  isShrank?: boolean;
  latestError?: DlError;
}

type Props = OwnProps;

export const StatusBox: React.FC<Props> = (props) => {
  const {errors, isShrank = false, latestError} = props;

  const intl = useIntl();
  const dispatch = useAppDispatch();

  const handleClearErrors = useCallback(() => {
    dispatch(statusSlice.actions.clearErrors());
  }, [dispatch]);

  const title = latestError
    ? TranslateUtils.translate(I18N.REPORTED_ERRORS, intl, {
        errorsLength: errors.length,
        latestError: latestError.getTitleMessage(),
      })
    : '';

  const hasTooltip = !!title;

  const {mouseEventEnter, mouseEventLeave, open, anchorEl} =
    useMuiAnchor(hasTooltip);

  return (
    <div className={styles.dlStatusBox}>
      {latestError && !isShrank ? (
        <div className={styles.dlStatusError}>
          <span>
            <span className={styles.dlStatusErrorTitle}>
              <FormattedMessage
                id={'status.error'}
                defaultMessage={'Error'}
                description={'label for application error message'}
              />
              {':'}
            </span>
            <span
              className={styles.dlStatusErrorText}
              onMouseEnter={mouseEventEnter}
              onMouseLeave={mouseEventLeave}
            >
              {latestError.getMessage(true)}
            </span>
          </span>
          <span className={styles.dlStatusClearErrors}>
            <RemoveButton
              removeCallback={handleClearErrors}
              item={DUMMY_ERROR}
              titleText={I18N.CLEAR_ERROR.id}
            />
          </span>
        </div>
      ) : null}
      <div className={styles.dlStatusRow}>
        <span
          className={classNames({
            [styles.dlStatusRowWord]: true,
            [styles.dlHide]: isShrank,
          })}
        >
          <FormattedMessage
            id={'status.error.status'}
            defaultMessage={'status'}
            description={'application status'}
          />
          {':'}
        </span>
        <i
          onMouseEnter={mouseEventEnter}
          onMouseLeave={mouseEventLeave}
          className={classNames({
            fas: true,
            'fa-check-circle': errors.length === 0,
            'fa-exclamation-triangle': errors.length > 0,
            [styles.dlStatusIconOk]: errors.length === 0,
            [styles.dlStatusIconError]: errors.length > 0,
          })}
        />
      </div>
      <DlMuiPopper hasTooltip={hasTooltip} anchorEl={anchorEl} open={open}>
        <Typography variant={'popper'}>{title}</Typography>
      </DlMuiPopper>
    </div>
  );
};

StatusBox.displayName = 'StatusBox';

StatusBox.defaultProps = {
  applicationLabel: '',
  isShrank: false,
  latestError: undefined,
  version: '',
};
